import { useEffect, useRef, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useSpark } from "@/lib/store";
import { ACTIVITIES, FEED_LABELS, MODES, activityPath } from "@/lib/activities";
import { ModeRhythm } from "@/components/ActivityMode";
import { patchProfile, getProfile } from "@/lib/api";
import ReflectionCard from "@/components/ReflectionCard";
import { TalkInput } from "@/components/TalkInput";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";

export default function Activity() {
  const { activityKey } = useParams();
  const { userId, profile, setProfile } = useSpark();
  const nav = useNavigate();
  const activity = ACTIVITIES.find(a => a.key === activityKey);
  const [draft, setDraft] = useState("");
  const [saveState, setSaveState] = useState("idle");
  const saveTimer = useRef(null);
  const loadedFor = useRef(null);

  useEffect(() => {
    if (!profile || loadedFor.current === `${userId}:${activityKey}`) return;
    loadedFor.current = `${userId}:${activityKey}`;
    setDraft(profile.activity_notes?.[activityKey]?.draft || "");
    setSaveState("idle");
  }, [profile, userId, activityKey]);

  if (!activity) {
    return (
      <div>
        <p className="text-[#56565D]">That activity doesn't exist yet.</p>
        <Link to="/part-one" className="spark-btn-secondary mt-4 inline-flex">Back to Part One</Link>
      </div>
    );
  }

  const mode = MODES[activity.mode];
  const idx = ACTIVITIES.findIndex(a => a.key === activityKey);
  const prevAct = ACTIVITIES[idx - 1];
  const nextAct = ACTIVITIES[idx + 1];
  const done = (profile?.completed_activities || []).includes(activityKey);

  const onDraft = (value) => {
    setDraft(value);
    clearTimeout(saveTimer.current);
    setSaveState("saving");
    saveTimer.current = setTimeout(async () => {
      const all = { ...(profile?.activity_notes || {}), [activityKey]: { draft: value } };
      try {
        setProfile(await patchProfile(userId, "activity_notes", all));
        setSaveState("saved");
      } catch {
        setSaveState("unsaved");
        saveTimer.current = setTimeout(() => onDraft(value), 4000);
      }
    }, 900);
  };

  const markComplete = async () => {
    const completed = Array.from(new Set([...(profile?.completed_activities || []), activityKey]));
    setProfile(await patchProfile(userId, "completed_activities", completed));
    toast("Held in your Career Compass.");
  };

  const onResolved = async (reflection) => {
    const p = await getProfile(userId);
    setProfile(p);
    if ((reflection.status === "confirmed" || reflection.status === "yes") && !p.completed_activities?.includes(activityKey)) {
      setProfile(await patchProfile(userId, "completed_activities", [...(p.completed_activities || []), activityKey]));
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <button data-testid="activity-back-btn" onClick={() => nav("/part-one")} className="text-sm text-[#8A8A91] hover:text-[#17171C] inline-flex items-center gap-1.5 mb-8">
        <ArrowLeft size={14} /> Part One
      </button>

      <header className="grid md:grid-cols-12 gap-8">
        <div className="md:col-span-8">
          <p className="spark-eyebrow-copper spark-eyebrow-with-dot">{activity.stage}</p>
          <h1 data-testid="activity-title" className="mt-3 text-4xl sm:text-5xl leading-tight text-[#17171C] font-medium tracking-tight">{activity.title}</h1>
          <p data-testid="activity-opening" className="mt-6 spark-voice text-2xl sm:text-3xl leading-snug text-[#17171C] max-w-2xl">{activity.opening}</p>
        </div>
        <aside className="md:col-span-4 md:pt-10 text-sm text-[#56565D] space-y-5">
          <div>
            <p className="spark-eyebrow mb-2">Why this matters</p>
            <p className="leading-relaxed">{activity.why}</p>
          </div>
          <div>
            <p className="spark-eyebrow mb-2">Feeds your Compass</p>
            <div className="flex flex-wrap gap-1.5">
              {activity.feeds.map(f => (
                <span key={f} className="rounded-full border border-[rgba(44,80,160,0.4)] bg-white px-2.5 py-1 text-xs text-[#1F3D80]">{FEED_LABELS[f]}</span>
              ))}
            </div>
          </div>
          <p className="text-xs text-[#8A8A91]">About {activity.minutes} minutes</p>
        </aside>
      </header>

      <div className="mt-12">
        <ModeRhythm mode={activity.mode} />
      </div>

      {activity.questions.length > 0 && (
        <section data-testid="activity-questions" className="mt-14 grid md:grid-cols-12 gap-6 md:gap-10">
          <div className="md:col-span-4">
            <p className="spark-eyebrow">{activity.mode === "reflect" ? "The questions we'll sit with" : activity.mode === "notice" ? "What Spark will do" : "The step"}</p>
          </div>
          <ul className="md:col-span-8 space-y-5">
            {activity.questions.map((q, i) => (
              <li key={i} className="flex items-baseline gap-5">
                <span className="text-sm tabular-nums text-[#8A8A91] w-6 shrink-0">{String(i + 1).padStart(2, "0")}</span>
                <p className={`${activity.mode === "notice" ? "text-[#56565D] leading-relaxed" : "spark-voice text-xl sm:text-2xl leading-snug text-[#17171C]"}`}>{q}</p>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="mt-14 grid md:grid-cols-12 gap-6 md:gap-10">
        <div className="md:col-span-4">
          <p className="spark-eyebrow">{mode.inputLabel}</p>
          <p className="mt-2 text-xs text-[#8A8A91]">Saves as you go.</p>
        </div>
        <div className="md:col-span-8">
          <TalkInput
            testid="activity-notes-input"
            rows={5}
            warm={activity.mode === "reflect"}
            value={draft}
            onChange={onDraft}
            placeholder={mode.placeholder}
          />
          <div className="mt-2 flex justify-end">
            <span data-testid="activity-save-state" className="text-xs text-[#8A8A91]">
              {saveState === "saving" ? "Saving…" : saveState === "saved" ? "Draft held" : saveState === "unsaved" ? "Couldn't save just now — retrying" : ""}
            </span>
          </div>
          <div className="mt-8">
            <ReflectionCard userId={userId} activityKey={activityKey} notes={draft} onResolved={onResolved} />
          </div>
        </div>
      </section>

      <div className="spark-thread mt-16" />

      <div className="mt-8 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-3">
          {prevAct && (
            <Link data-testid="activity-prev-btn" to={activityPath(prevAct)} className="spark-btn-secondary">← {prevAct.title}</Link>
          )}
          {!done ? (
            <button data-testid="activity-mark-complete-btn" onClick={markComplete} className="spark-btn-secondary">Hold in my Compass</button>
          ) : (
            <span data-testid="activity-done-badge" className="inline-flex items-center gap-2 text-sm text-[#8A8A91] px-2">
              <span className="h-1.5 w-1.5 rounded-full bg-[#2C50A0]" /> Held in your Compass
            </span>
          )}
        </div>
        {nextAct ? (
          <Link data-testid="activity-next-btn" to={activityPath(nextAct)} className="spark-btn-primary">Next · {nextAct.title}</Link>
        ) : (
          <Link data-testid="activity-part-two-btn" to="/part-two" className="spark-btn-primary">Okay. What's next?</Link>
        )}
      </div>

      <p className="mt-10 text-xs text-[#8A8A91] max-w-lg">
        Placeholder — this shows the intended interaction and rhythm. Sequence, naming and format may still change.
      </p>
    </div>
  );
}
